<script lang="ts" setup>
import { ref, inject, watch, computed } from 'vue';

import type { Ref } from 'vue';
import type { Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { Perspectives } from '@/viewerExtensions';

interface IProps {
  zoomRestriction: [number, number];
}

interface IEmits {
  (e: 'change', value: [number, number]): unknown;
}

const emit = defineEmits<IEmits>();
const props = defineProps<IProps>();

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const zoomRestrictionMin = ref(props.zoomRestriction[0]);
const zoomRestrictionMax = ref(props.zoomRestriction[1]);
const previewRestrictions = inject('previewRestrictions') as Ref<boolean>;
const perspectivesExtension = viewer.value?.extensions.get('Perspectives') as Extension<Perspectives>;

const computedZoomRestriction = computed<[number, number]>(() => [zoomRestrictionMin.value, zoomRestrictionMax.value]);

const zoomRestrictionWatcher = () => {
  if (previewRestrictions.value === true) {
    perspectivesExtension.setZoomRestriction(computedZoomRestriction.value);
  } else {
    perspectivesExtension.resetZoomRestriction();
  }

  emit('change', computedZoomRestriction.value);
};

const previewRestrictionsWatcher = (newValue: boolean) => {
  if (newValue === true) {
    perspectivesExtension.setZoomRestriction(computedZoomRestriction.value);
  } else {
    perspectivesExtension.resetZoomRestriction();
  }
};

watch(computedZoomRestriction, zoomRestrictionWatcher, { deep: true });
watch(previewRestrictions, previewRestrictionsWatcher);
</script>

<template>
  <div class="flex flex-row justify-between">
    <div class="flex flex-row flex-1 items-center mr-3">
      <label for="perspective-zoom-min" class="text-xs mr-1">Min</label>
      <input
        id="perspective-zoom-min"
        v-model="zoomRestrictionMin"
        class="border text-xs p-1 w-full"
        type="number"
        min="0"
        max="10"
        step="0.01"
      >
    </div>
    <div class="flex flex-row flex-1 items-center ml-3">
      <label for="perspective-zoom-max" class="text-xs mr-1">Max</label>
      <input
        id="perspective-zoom-max"
        v-model="zoomRestrictionMax"
        class="border text-xs p-1 w-full"
        type="number"
        min="0"
        max="10"
        step="0.01"
      >
    </div>
  </div>
</template>
